import React, { useContext, useState, useEffect, useRef, use } from 'react';
import styled from 'styled-components';

import CheckboxSettingsField from './CheckboxSettingsField';
import InputSettingsField from './InputSettingsField';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import SettingsTabs from './SettingsTabs';
import TooltipSettings from './TooltipSettings';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import TitleSettings from './TitleSettings';
import DateFormatSettings from './DateFormatSettings';
import { StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldTypes from '../../../constants/FieldTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';
import { validateDateTimeFormat } from '../../../lib/dateTimeHelper';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

/**
 * These field settings should be used for the following fields:
 * - text
 * - textarea
 * - rich text field
 * - date
 * - number
 * - uniqueId
 */
const FieldSettingsTextField = ({

  //Logic properties
  fileId,
  sortedPages,
  fields,
  logic,

  view,
  fieldId,
  fieldIdentifier,
  fieldPositionId,
  fieldSection,

  title,
  type,
  format,
  x,
  y,
  width,
  height,
  value,
  metadata,
  config,
  hidden,
  disabled,
  formula,

  //API Developers
  identifier,
  required,

  //Title styles
  titleDisplay,
  titleFontSize,
  titleFontColor,
  titleTextAlign,
  titleFontStyle,
  titleFontWeight,
  titleTextTransform,
  titleTextDecoration,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  margin,
  padding,
  lineHeight,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onDuplicate,

  tipVisible,
  tipTitle,
  tipDescription,
  useReadableIds,

}) => {


  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');
  const [formatError, setFormatError] = useState('');

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== fieldId && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = fieldId;

  }, [fieldId, setReadableIdError]);


  const isStylesHidden = isSettingHidden(config, 'styles');
  const isTitleHidden = isSettingHidden(config, 'title');
  const isValueHidden = isSettingHidden(config, 'value');
  const isFormatHidden = isSettingHidden(config, 'format');
  const isDisabledHidden = isSettingHidden(config, 'disabled');
  const isRequiredHidden = isSettingHidden(config, 'required');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');
  const isTooltipHidden = isSettingHidden(config, 'tooltip');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isIdHidden = isSettingHidden(config, '_id');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Text Styles
  const isLineHeightHidden = isStyleSettingHidden(config, 'lineHeight');
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = type === FieldTypes.rte || isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isHeaderOrFooter = fieldSection === 'header' || fieldSection === 'footer';

  const metadataOptions = getMetadataOptions(config);


  let nextFieldSettingsIcon = '';
  if (type === FieldTypes.text) nextFieldSettingsIcon = ThemeIcons.fields.text;
  if (type === FieldTypes.number) nextFieldSettingsIcon = ThemeIcons.fields.number;
  if (type === FieldTypes.date) nextFieldSettingsIcon = ThemeIcons.fields.date;
  if (type === FieldTypes.textarea) nextFieldSettingsIcon = ThemeIcons.fields.textarea;

  const defaultValueNode = isValueHidden || (type !== FieldTypes.text && type !== FieldTypes.textarea) ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="Default"
        fieldId={fieldId}
        value={value}
        type={type}
        rows={3}
        onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { value })}
      />
    </StyledFieldWrapper>
  );


  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);
          
          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );

  const formatNode = isFormatHidden || type !== FieldTypes.date ? null : (
    <>
      <DateFormatSettings
        format={format}
        onChange={(value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { format: value })}
        customDateTimeFormatEnabled={config?.customDateTimeFormat}
      />
      <DividerLine />
    </>
  );

  const stylesNode = (
    <>
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Title"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontSize: value }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontColor: value }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextAlign: value }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontStyle: value }),

                fontWeight: titleFontWeight,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleFontWeight: value }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextTransform: value }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { titleTextDecoration: value }),
              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden
      ) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Text"
            height={type === FieldTypes.textarea ? 210 : 170}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                lineHeight: lineHeight || 10,
                onLineHeightChange: isLineHeightHidden || (type !== FieldTypes.textarea && type !== FieldTypes.rte) ? null : (value) => {
                  if (value) onChange(fieldId, fieldIdentifier, fieldPositionId, { lineHeight: value });
                },

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={400}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                view,
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Title</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <TitleSettings
            {...{
              title,
              titleDisplay
            }}
            onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
          />
        </>
      )}
      {(isValueHidden
        && isFormatHidden
        && isRequiredHidden
        && isDisabledHidden
        && isHiddenHidden
      ) ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
      )}
      {defaultValueNode}
      {formatNode}
      {isDisabledHidden ? null : (
        <StyledFieldWrapper>
          <CheckboxSettingsField
            icon={ThemeIcons.settings.disabled}
            iconColor={disabled ? theme.primaryColor : theme.borderColor}
            id="disabled"
            label="Read-only"
            selected={disabled}
            onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { disabled: selected },)}
          />
        </StyledFieldWrapper>
      )}
      {isRequiredHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.required}
              iconColor={required ? theme.dangerColor : theme.borderColor}
              iconSize="1x"
              id="required"
              label="Required"
              selected={required}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { required: selected },)}
            />
          </StyledFieldWrapper>
        )
      )}
      {isHiddenHidden ? null : (
        !isHeaderOrFooter && (
          <StyledFieldWrapper>
            <CheckboxSettingsField
              icon={ThemeIcons.settings.hide}
              iconColor={hidden ? theme.warningColor : theme.borderColor}
              id="hidden"
              label="Hidden"
              selected={hidden}
              onChange={(selected) => onChange(fieldId, fieldIdentifier, fieldPositionId, { hidden: selected },)}
            />
          </StyledFieldWrapper>
        )
      )}
      {isLogicHidden || fields.length < 2 ? null : (
        !isHeaderOrFooter && (
          <>
            <DividerLine />
            <StyledFieldWrapper>
              <ConditionalLogicSettings
                fileId={fileId}
                targetLabel=" field"
                targetFieldId={fieldId}
                sortedPages={sortedPages}
                fields={fields}
                logic={logic}
                onSubmit={(nextLogic) => onChange(fieldId, fieldIdentifier, fieldPositionId, { logic: nextLogic })}
              />
            </StyledFieldWrapper>
          </>
        )
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isTooltipHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title="Tooltip"
            height={166}
          >
            <TooltipSettings
              {...{
                tipVisible,
                tipTitle,
                tipDescription
              }}
              onChange={(changes) => onChange(fieldId, fieldIdentifier, fieldPositionId, changes)}
            />
          </AccordionSettings>
        </>
      )}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 0 }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifier'}
            height={60}
          >
            <IdentifierSelectorSettings
              fieldId={fieldId}
              fieldType={type}
              type="text"
              customValue={config?.identifier?.options}
              enableCustomOption={config?.identifier?.custom}
              value={identifier}
              onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
            />
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />

            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => onDuplicate(fieldId, fieldPositionId)}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title="Settings"
        icon={nextFieldSettingsIcon}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

export default FieldSettingsTextField;
